import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Sentry } from '@idk-web/core-utils';
import { useIsMounted } from '@idk-web/core-ui';
import { InboxSignResponse } from '@idk-web/api';
import { isBankIDError, translateError } from '@/utils/error';
import * as inboxSign from '@/hooks/inbox/sign/inboxSign';
import { StartedInboxSignRequest } from '@/hooks/inbox/sign/inboxSign';

type State =
  | {
      state: 'IDLE';
    }
  | {
      state: 'CANCELLED';
    }
  | {
      state: 'TIMED_OUT';
    }
  | {
      state: 'WAITING';
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
    }
  | {
      state: 'COMPLETE';
    }
  | {
      state: 'FAILED';
      error: string;
    };

export type InboxSign = {
  signSweden(messageId: string): Promise<void>;
  cancel(): Promise<void>;
  reset(): void;
} & State;

export function useInboxSign(): InboxSign {
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const request = useRef<StartedInboxSignRequest>();
  const [state, setState] = useState<State>({ state: 'IDLE' });
  const onInit = useCallback(
    async (data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>) => {
      onProgress(data);
    },
    [],
  );
  const onComplete = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'COMPLETE' });
    }

    request.current = undefined;
  }, []);
  const onError = useCallback(
    async (error: unknown) => {
      if (isMounted()) {
        if (error === 'cancelled') {
          setState({ state: 'CANCELLED' });
        } else if (error === 'timed out') {
          setState({ state: 'TIMED_OUT' });
        } else {
          if (!isBankIDError(error)) {
            Sentry.reportError('Inbox sign failed', { error });
          }

          setState({ state: 'FAILED', error: translateError(t, error) });
        }
      }

      request.current = undefined;
    },
    [t],
  );
  const onProgress = useCallback(
    async (data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>) => {
      setState((oldState) => {
        let newState: State;
        if (data.bankId) {
          newState = { state: 'WAITING', bankId: data.bankId };
        } else {
          newState = { state: 'WAITING' };
        }

        if (JSON.stringify(oldState) === JSON.stringify(newState)) {
          return oldState;
        } else {
          return newState;
        }
      });
    },
    [],
  );
  const signSweden = useCallback(
    async (messageId: string): Promise<void> => {
      setState({ state: 'WAITING' });

      request.current = inboxSign
        .sweden({ messageId })
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const cancel = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'CANCELLED' });
    }

    return request.current?.cancel();
  }, []);
  const reset = useCallback(() => {
    if (isMounted()) {
      setState({ state: 'IDLE' });
    }

    request.current = undefined;
  }, []);

  return useMemo(
    () => ({
      signSweden,
      cancel,
      reset,
      ...state,
    }),
    [signSweden, cancel, reset, state],
  );
}
